/**
 * PhonePreview
 *
 * Live preview panel showing how the survey flow appears on a phone frame,
 * updating with form state, theme selection, and explainer video presence.
 */
import React from 'react';
import { Upload, ChevronRight, ChevronLeft, ChevronLeft as SafariBack, ChevronRight as SafariForward, Share2, MoreVertical, Video } from 'lucide-react';
import { Iphone15Pro } from './iphone';
import { themes as sharedThemes } from '../../testimonials/survey/themes';

const steps = [
  { id: 'intro', title: 'Welcome' },
  { id: 'contact', title: 'Contact Info' },
  { id: 'location', title: 'Location' },
  { id: 'response', title: 'Your Response' }
];

export function PhonePreview({ 
  selectedTheme = 'sunset', 
  previewImage, 
  formData = {}, 
  surveyQuestions = [],
  currentStep = 0,
  gradientColors = {
    from: '#1a365d',
    via: '#3182ce',
    to: '#2c5282'
  },
  gradientDirection = 'br',
  hexText = '#ffffff',
  themes = [],
  hasExplainerVideo = false,
  explainerVideo = null,
  isSettingsFlow = false
}) {
  // Ensure surveyQuestions is always an array
  const safeQuestions = Array.isArray(surveyQuestions) ? surveyQuestions : [];
  
  const [previewStep, setPreviewStep] = React.useState('intro');
  const [previewFormData, setPreviewFormData] = React.useState({
    firstName: '',
    lastName: '',
    email: '',
    zipCode: ''
  });

  // Log when previewImage changes
  React.useEffect(() => {
    console.log('PhonePreview: previewImage updated:', {
      previewImage: previewImage ? 'exists' : 'null',
      currentStep,
      selectedTheme,
      previewStep,
      surveyQuestionsLength: safeQuestions.length
    });
    
    // Add specific debugging for steps 3 and 4
    if (currentStep === 3) {
      console.log('PhonePreview: Design step (3) - previewImage:', previewImage ? 'exists' : 'null');
    } else if (currentStep === 4) {
      console.log('PhonePreview: BasicInfo step (4) - previewImage:', previewImage ? 'exists' : 'null');
    }
  }, [previewImage, currentStep, selectedTheme, previewStep, safeQuestions]);

  // Use provided themes, or fall back to shared themes
  const availableThemes = themes && Object.keys(themes).length > 0 ? themes : sharedThemes;
  
  // Show appropriate preview content based on the current form step
  React.useEffect(() => {
    if (isSettingsFlow) {
      // Special mapping for campaign settings flow
      // Original steps: 0=Internal, 1=Category, 2=Subcategory, 3=Design, 4=BasicInfo, 5=Details, 6=Review
      if (currentStep === 0) {
        // Internal Name step
        setPreviewStep('intro');
      } else if (currentStep === 3) {
        // Design step
        setPreviewStep('intro');
      } else if (currentStep === 4) {
        // Basic Info step (title, description, image)
        setPreviewStep('intro');
      } else if (currentStep === 5) {
        // Campaign Details step (questions, explainer video)
        setPreviewStep('response');
      } else if (currentStep === 6) {
        // Review step
        setPreviewStep('response');
      } else {
        // Default to intro for other steps
        setPreviewStep('intro');
      }
      
      console.log('PhonePreview (Settings Flow): Step changed to', currentStep, '- setting preview to:', 
        currentStep === 5 || currentStep === 6 ? 'response' : 'intro');
    } else {
      // Original behavior for create campaign flow
      // Map currentStep to the appropriate preview content
      // Original steps: 0=Internal, 1=Category, 2=Subcategory, 3=Design, 4=BasicInfo, 5=Details, 6=Review
      if (currentStep === 0) {
        // Internal Name step
        setPreviewStep('intro');
      } else if (currentStep === 3) {
        // Design step
        setPreviewStep('intro');
      } else if (currentStep === 4) {
        // Campaign Details step (questions, explainer video)
        setPreviewStep('response');
      } else if (currentStep === 6) {
        // Review step
        setPreviewStep('response');
      } else {
        // Default to intro for other steps
        setPreviewStep('intro');
      }
      
      console.log('PhonePreview (Create Flow): Step changed to', currentStep, '- setting preview to:', 
        currentStep === 4 ? 'response' : 'intro');
    }
  }, [currentStep, isSettingsFlow]); // Run when currentStep or isSettingsFlow changes

  const currentStepIndex = steps.findIndex(step => step.id === previewStep);
  const isFirstStep = currentStepIndex === 0;
  const isLastStep = currentStepIndex === steps.length - 1;
  
  const goToNextStep = () => !isLastStep && setPreviewStep(steps[currentStepIndex + 1].id);
  const goToPreviousStep = () => !isFirstStep && setPreviewStep(steps[currentStepIndex - 1].id);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setPreviewFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Generate custom gradient style for custom theme
  const getCustomGradientStyle = () => {
    if (selectedTheme !== 'custom') return null;
    
    const direction = gradientDirection === 'br' ? '135deg' :
                     gradientDirection === 'r' ? '90deg' :
                     gradientDirection === 'b' ? '180deg' :
                     gradientDirection === 'bl' ? '225deg' : '135deg';
                     
    return `linear-gradient(${direction}, ${gradientColors.from}, ${gradientColors.via}, ${gradientColors.to})`;
  };
  
  // Custom theme object for rendering
  const customTheme = {
    background: '',  // This will be overridden by inline style
    text: '',  // Will be set with inline style
    subtext: '', // Will be set with inline style
    border: 'border-white/20',
    input: 'bg-white/20',
    name: 'Custom Theme'
  };
  
  // Determine which theme to use
  const themeToUse = selectedTheme === 'custom' ? customTheme : availableThemes[selectedTheme] || availableThemes.sunset;

  // Get text style for custom theme
  const getTextStyle = () => {
    if (selectedTheme !== 'custom') return {};
    return { color: hexText };
  };

  // Get subtext style for custom theme
  const getSubtextStyle = () => {
    if (selectedTheme !== 'custom') return {};
    // Create a slightly transparent version of the text color
    return { color: hexText, opacity: 0.8 };
  };

  return (
    <div className="hidden lg:block sticky top-16 h-[calc(100vh-6rem)] w-[500px] xl:block">
      {console.log('PhonePreview rendering with:', {
        currentStep,
        previewStep,
        previewImage: previewImage ? 'exists' : 'null',
        formData: {
          title: formData.title || 'No title',
          description: formData.description || 'No description'
        },
        selectedTheme,
        gradientColors,
        customGradientStyle: selectedTheme === 'custom' ? getCustomGradientStyle() : 'not custom'
      })}
      <div className="flex flex-col items-center">
        <div className="scale-[0.8] origin-top xl:scale-[0.85] -mt-20">
          <Iphone15Pro>
            <div 
              className={`h-full ${(currentStep === 0 || currentStep === 1) ? 'bg-gray-100 dark:bg-gray-800' : selectedTheme === 'custom' ? '' : themeToUse.background} transition-colors duration-200`}
              style={selectedTheme === 'custom' && !(currentStep === 0 || currentStep === 1) ? 
                { background: getCustomGradientStyle() } : {}}
            >
              <div className="flex flex-col h-full">
                {/* Safari URL Bar */}
                <div className="h-[120px] flex items-end px-4 pb-4">
                  <div className={`w-full ${(currentStep === 0 || currentStep === 1) ? 'bg-gray-200/90 dark:bg-gray-700/90' : 'bg-white/10'} backdrop-blur-lg rounded-xl p-2 flex items-center gap-2`}>
                    <div className="flex items-center gap-1">
                      <button className={`p-1 ${(currentStep === 0 || currentStep === 1) ? 'text-gray-500 dark:text-gray-400' : 'text-white/50'}`}>
                        <SafariBack className="w-4 h-4" />
                      </button>
                      <button className={`p-1 ${(currentStep === 0 || currentStep === 1) ? 'text-gray-500 dark:text-gray-400' : 'text-white/50'}`}>
                        <SafariForward className="w-4 h-4" />
                      </button>
                    </div>
                    <div className={`flex-1 ${(currentStep === 0 || currentStep === 1) ? 'bg-white dark:bg-gray-600' : 'bg-white/10'} rounded-lg px-3 py-1.5 text-sm ${(currentStep === 0 || currentStep === 1) ? 'text-gray-700 dark:text-gray-300' : 'text-white/70'} truncate`}>
                      advocacylab.io/survey/preview
                    </div>
                    <div className="flex items-center gap-1">
                      <button className={`p-1 ${(currentStep === 0 || currentStep === 1) ? 'text-gray-500 dark:text-gray-400' : 'text-white/50'}`}>
                        <Share2 className="w-4 h-4" />
                      </button>
                      <button className={`p-1 ${(currentStep === 0 || currentStep === 1) ? 'text-gray-500 dark:text-gray-400' : 'text-white/50'}`}>
                        <MoreVertical className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
                
                {/* Progress Bar */}
                <div className={`h-1 ${themeToUse.border} bg-white/10`}>
                  <div
                    className={`h-1 bg-white/20 transition-all duration-500`}
                    style={{ width: `${((currentStepIndex + 1) / steps.length) * 100}%` }}
                  />
                </div>

                <div className="p-6">
                  {/* Introduction or Internal Name Step - Blank Preview */}
                  {(currentStep === 0 || currentStep === 1) && (
                    <div className="text-center flex flex-col items-center justify-center h-[500px] rounded-xl p-8">
                      <div className="max-w-xs">
                        <h1 className={`text-2xl font-bold tracking-tight text-gray-800 dark:text-white mb-3`}>
                          Preview
                        </h1>
                        <p className={`text-gray-600 dark:text-gray-300`}>
                          Your campaign will appear here as you build it.
                        </p>
                      </div>
                    </div>
                  )}

                  {/* For steps 2-5, we're basing content on previewStep */}
                  {currentStep > 1 && (
                    <>
                      {/* Welcome/Intro Screen - shows campaign title/description and Get Started button */}
                      {previewStep === 'intro' && (
                        <div className="text-center">
                          {previewImage ? (
                            <div className="flex justify-center mb-6">
                              <img 
                                src={previewImage} 
                                alt="Campaign preview" 
                                className="w-4/5 h-auto object-contain max-h-[200px]" 
                                onLoad={() => console.log('PhonePreview: Image loaded successfully')}
                                onError={(e) => {
                                  console.error('Error loading image in PhonePreview:', e);
                                  e.target.onerror = null;
                                  e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwsIHNhbnMtc2VyaWYiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGRvbWluYW50LWJhc2VsaW5lPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==';
                                }}
                              />
                            </div>
                          ) : (
                            <div className="flex justify-center mb-6">
                              <div className={`w-4/5 h-[150px] rounded-lg flex items-center justify-center ${themeToUse.border} border-2 shadow-md`}>
                                <Upload className={`w-8 h-8 ${selectedTheme === 'custom' ? '' : themeToUse.text} opacity-50`} 
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                />
                              </div>
                            </div>
                          )}
                          <h1 className={`text-3xl font-bold tracking-tight ${selectedTheme === 'custom' ? '' : themeToUse.text} mb-2`}
                            style={selectedTheme === 'custom' ? getTextStyle() : {}}
                          >
                            {formData.title || 'Campaign Title'}
                          </h1>
                          <p className={`mt-4 text-base ${selectedTheme === 'custom' ? '' : themeToUse.subtext}`}
                            style={selectedTheme === 'custom' ? getSubtextStyle() : {}}
                          >
                            {formData.description || 'Campaign Description'}
                          </p>
                          <button
                            onClick={goToNextStep}
                            className={`mt-8 inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
                              themeToUse.border} border-2 ${selectedTheme === 'custom' ? '' : themeToUse.text} hover:bg-white/10`}
                            style={selectedTheme === 'custom' ? getTextStyle() : {}}
                          >
                            Get Started
                            <ChevronRight className="h-4 w-4" />
                          </button>
                        </div>
                      )}
                      
                      {/* Contact Info Screen */}
                      {previewStep === 'contact' && (
                        <div>
                          <h2 className={`text-2xl font-bold ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                            style={selectedTheme === 'custom' ? getTextStyle() : {}}
                          >Contact Information</h2>
                          <p className={`mt-2 text-sm ${selectedTheme === 'custom' ? '' : themeToUse.subtext}`}
                            style={selectedTheme === 'custom' ? getSubtextStyle() : {}}
                          >
                            Please provide your contact details. This information will be kept private.
                          </p>
                          <div className="mt-8 space-y-6">
                            <div className="grid grid-cols-1 gap-6">
                              <div>
                                <label className={`block text-sm font-medium ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                >First name</label>
                                <input
                                  name="firstName"
                                  value={previewFormData.firstName}
                                  onChange={handleInputChange}
                                  className={`mt-2 block w-full rounded-lg ${themeToUse.border} ${themeToUse.input} ${selectedTheme === 'custom' ? '' : themeToUse.text} placeholder:${selectedTheme === 'custom' ? '' : themeToUse.text}/50 focus:ring-white/20 px-3 py-2`}
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                />
                              </div>
                              <div>
                                <label className={`block text-sm font-medium ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                >Last name</label>
                                <input
                                  name="lastName"
                                  value={previewFormData.lastName}
                                  onChange={handleInputChange}
                                  className={`mt-2 block w-full rounded-lg ${themeToUse.border} ${themeToUse.input} ${selectedTheme === 'custom' ? '' : themeToUse.text} placeholder:${selectedTheme === 'custom' ? '' : themeToUse.text}/50 focus:ring-white/20 px-3 py-2`}
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                />
                              </div>
                              <div>
                                <label className={`block text-sm font-medium ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                >Email address</label>
                                <input
                                  name="email"
                                  type="email"
                                  value={previewFormData.email}
                                  onChange={handleInputChange}
                                  className={`mt-2 block w-full rounded-lg ${themeToUse.border} ${themeToUse.input} ${selectedTheme === 'custom' ? '' : themeToUse.text} placeholder:${selectedTheme === 'custom' ? '' : themeToUse.text}/50 focus:ring-white/20 px-3 py-2`}
                                  style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      )}

                      {/* Location Screen */}
                      {previewStep === 'location' && (
                        <div>
                          <h2 className={`text-2xl font-bold ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                            style={selectedTheme === 'custom' ? getTextStyle() : {}}
                          >Location</h2>
                          <p className={`mt-2 text-sm ${selectedTheme === 'custom' ? '' : themeToUse.subtext}`}
                            style={selectedTheme === 'custom' ? getSubtextStyle() : {}}
                          >
                            Please provide your zip code to help us understand our community better.
                          </p>
                          <div className="mt-8">
                            <label className={`block text-sm font-medium ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                              style={selectedTheme === 'custom' ? getTextStyle() : {}}
                            >Zip code</label>
                            <input
                              name="zipCode"
                              value={previewFormData.zipCode}
                              onChange={handleInputChange}
                              maxLength={5}
                              pattern="[0-9]*"
                              className={`mt-2 block w-full rounded-lg ${themeToUse.border} ${themeToUse.input} ${selectedTheme === 'custom' ? '' : themeToUse.text} placeholder:${selectedTheme === 'custom' ? '' : themeToUse.text}/50 focus:ring-white/20 px-3 py-2`}
                              style={selectedTheme === 'custom' ? getTextStyle() : {}}
                            />
                          </div>
                        </div>
                      )}

                      {/* Questions/Response Screen */}
                      {previewStep === 'response' && (
                        <div>
                          {/* Questions Panel */}
                          <div>
                            {/* Explainer Video (if enabled) */}
                            {hasExplainerVideo && (
                              <div className="mb-6">
                                <div className="rounded-lg overflow-hidden w-full">
                                  {explainerVideo ? (
                                    typeof explainerVideo === 'string' && explainerVideo.startsWith('http') ? (
                                      <div className="aspect-video w-full">
                                        <iframe 
                                          src={explainerVideo.replace('youtube.com/watch?v=', 'youtube.com/embed/').replace('youtu.be/', 'youtube.com/embed/')}
                                          className="w-full h-full" 
                                          allowFullScreen
                                          title="YouTube Explainer Video"
                                          frameBorder="0"
                                        />
                                      </div>
                                    ) : (
                                      <video 
                                        src={explainerVideo} 
                                        controls 
                                        className="w-full h-auto max-h-[150px]"
                                      />
                                    )
                                  ) : (
                                    <div className="bg-black/20 h-[150px] flex items-center justify-center w-full">
                                      <div className="text-center">
                                        <Video className={`h-8 w-8 mx-auto mb-2 ${selectedTheme === 'custom' ? '' : themeToUse.text} opacity-60`}
                                          style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                        />
                                        <p className={`text-xs ${selectedTheme === 'custom' ? '' : themeToUse.text} opacity-60`}
                                          style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                        >
                                          Explainer Video
                                        </p>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              </div>
                            )}

                            <h2 className={`text-2xl font-bold ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                              style={selectedTheme === 'custom' ? getTextStyle() : {}}
                            >Questions</h2>
                            <p className={`mt-2 text-sm ${selectedTheme === 'custom' ? '' : themeToUse.subtext}`}
                              style={selectedTheme === 'custom' ? getSubtextStyle() : {}}
                            >
                              Choose one or more questions to answer in your video response.
                            </p>
                            <div className="mt-6 space-y-4">
                              {safeQuestions.map((question, index) => (
                                <div key={index} className={`rounded-lg ${themeToUse.border} border p-4`}>
                                  <p className={`text-sm font-medium ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                                    style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                  >
                                    {question.question}
                                  </p>
                                </div>
                              ))}
                              {safeQuestions.length === 0 && (
                                <div className={`rounded-lg ${themeToUse.border} border p-4`}>
                                  <p className={`text-sm font-medium ${selectedTheme === 'custom' ? '' : themeToUse.text}`}
                                    style={selectedTheme === 'custom' ? getTextStyle() : {}}
                                  >
                                    Sample question: What's your opinion on this topic?
                                  </p>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </div>

                {/* Navigation Buttons - Always show for steps 1-4 when not on the intro screen */}
                {currentStep > 0 && previewStep !== 'intro' && (
                  <div className="mt-auto p-6 flex justify-between">
                    <button
                      onClick={goToPreviousStep}
                      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
                        themeToUse.border} border-2 ${selectedTheme === 'custom' ? '' : themeToUse.text} hover:bg-white/10`}
                      style={selectedTheme === 'custom' ? getTextStyle() : {}}
                    >
                      <ChevronLeft className="h-4 w-4" />
                      Back
                    </button>
                    <button
                      onClick={goToNextStep}
                      className={`inline-flex items-center gap-2 rounded-lg px-4 py-2.5 text-sm font-medium ${
                        themeToUse.border} border-2 ${selectedTheme === 'custom' ? '' : themeToUse.text} hover:bg-white/10`}
                      style={selectedTheme === 'custom' ? getTextStyle() : {}}
                    >
                      {previewStep === 'response' ? 'Upload Video' : 'Continue'}
                      {previewStep !== 'response' && <ChevronRight className="h-4 w-4" />}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </Iphone15Pro>
        </div>
      </div>
    </div>
  );
}